<template>
    <div class="loading-container" v-if="shouldShowLoading">
        <!-- SVG Animation (MosCraciun) -->
        <div v-if="loadingType === 'svg'" class="svg-animation-container">
            <!-- Scene with parallax trees -->
            <div class="scene">
                <div class="layer layer--far" aria-hidden="true"></div>
                <div class="layer layer--mid" aria-hidden="true"></div>

                <!-- Frog row -->
                <div class="frog-wrapper" aria-label="loading">
                    <div class="frog-mover">
                        <img src="/Loading_broscuta.svg" alt="Frog" class="frog" />
                        <!-- Speech bubble -->
                        <!-- <div class="speech-bubble">
                            <span class="bubble-text">
                                <span class="word word-1">Ho</span>
                                <span class="word word-2">ho</span>
                                <span class="word word-3">ho</span>
                            </span>
                        </div> -->
                    </div>
                </div>
            </div>

            <!-- Progress line -->
            <div class="line-track">
                <div class="line-fill"></div>
            </div>
        </div>

        <!-- Video player -->
        <video
            v-else-if="loadingType === 'video'"
            ref="loadingVideo"
            class="loading-video"
            :autoplay="!waitForVideoCompletion"
            :loop="!waitForVideoCompletion"
            muted
            playsinline
            webkit-playsinline
            preload="auto"
            @error="onVideoError"
            @canplay="onVideoCanPlay"
            @loadedmetadata="onVideoLoadedMetadata"
            @ended="onVideoEnded"
        >
           
            <!-- <source src="/christmas.mp4" type="video/mp4; codecs=avc1.42E01E, mp4a.40.2" />
            <source src="/christmas.mp4" type="video/mp4" /> -->
            <source src="/easter.mp4" type="video/mp4" />
            <!-- Fallback message if video doesn't load -->
            <p>Your browser does not support the video tag or the video format.</p>
        </video>
    </div>
</template>

<script>
export default {
    name: "LoadingComponent",
    props: {
        isLoading: { type: Boolean, default: false },
        /**
         * Type of loading animation to display.
         * Options: 'svg' (MosCraciun animation) or 'video' (christmas.mov)
         * Default: 'svg'
         */
        loadingType: {
            type: String,
            default: "video", // Change to "video" to use video instead
            validator: (value) => ["svg", "video"].includes(value),
        },
        /**
         * If true, video will loop while loading. If false, video will play once and wait for completion.
         * Only applies when loadingType is 'video'.
         * Default: true
         */
        waitForVideoCompletion: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            playPromise: null,
            videoCompleted: false,
        };
    },
    computed: {
        /**
         * Determines if loading screen should be shown.
         * Shows loading screen based on isLoading prop.
         */
        shouldShowLoading() {
            return this.isLoading;
        },
    },
    watch: {
        isLoading(newVal) {
            if (newVal && this.loadingType === "video") {
                // Reset video completion state when loading starts
                this.videoCompleted = false;
                this.$nextTick(() => {
                    this.playVideo();
                });
            } else if (!newVal && this.loadingType === "video") {
                // Stop video immediately when loading finishes
                this.stopVideo();
            }
        },
    },
    mounted() {
        // Ensure video plays when component mounts
        if (this.isLoading && this.loadingType === "video") {
            this.$nextTick(() => {
                this.playVideo();
            });
        }
    },
    beforeUnmount() {
        // Clean up video playback when component is being destroyed
        this.stopVideo();
    },
    methods: {
        async playVideo() {
            const video = this.$refs.loadingVideo;
            if (!video) {
                console.warn("Video element not found");
                return;
            }

            // If there's a pending play promise, wait for it or abort
            if (this.playPromise !== null) {
                try {
                    await this.playPromise;
                } catch (e) {
                    // Previous play was interrupted, that's okay
                }
            }

            // Check if video is still in the document
            if (!document.contains(video)) {
                return;
            }

            // Wait for video to be ready
            if (video.readyState < 2) {
                // Video not loaded yet, wait for it
                await new Promise((resolve) => {
                    video.addEventListener("canplay", resolve, { once: true });
                    video.addEventListener("error", resolve, { once: true });
                });
            }

            // Check again if video is still in document
            if (!document.contains(video)) {
                return;
            }

            try {
                // Store the promise to handle interruptions
                this.playPromise = video.play();
                
                if (this.playPromise !== undefined) {
                    await this.playPromise;
                }
            } catch (error) {
                // Auto-play was prevented or interrupted
                // Only log if it's not an abort error (which is expected when component unmounts)
                if (error.name !== "AbortError" && error.name !== "NotAllowedError") {
                    console.warn("Video autoplay prevented:", error);
                }
            } finally {
                // Clear the promise after handling
                this.playPromise = null;
            }
        },
        stopVideo() {
            const video = this.$refs.loadingVideo;
            if (video) {
                // Pause the video
                video.pause();
                // Reset the play promise
                this.playPromise = null;
            }
        },
        onVideoError(event) {
            const video = event.target;
            const errorDetails = {
                error: video.error,
                code: video.error?.code,
                message: video.error?.message,
                networkState: video.networkState,
                readyState: video.readyState,
                src: video.currentSrc || video.src,
            };
            console.error("Video error:", errorDetails);
            
            // iOS-specific: Check if it's a codec issue
            if (video.error?.code === 4) {
                console.error("MEDIA_ERR_SRC_NOT_SUPPORTED: Video codec may not be supported on iOS. Ensure video is encoded with H.264 codec.");
            }
        },
        onVideoLoadedMetadata() {
            // iOS Safari: Ensure video metadata is loaded before playing
            const video = this.$refs.loadingVideo;
            if (video && this.isLoading) {
                // Check if video has valid dimensions (iOS requirement)
                if (video.videoWidth > 0 && video.videoHeight > 0) {
                    this.playVideo();
                } else {
                    console.warn("Video metadata loaded but dimensions are invalid");
                }
            }
        },
        onVideoCanPlay() {
            if (this.isLoading) {
                this.playVideo();
            }
        },
        onVideoEnded() {
            this.videoCompleted = true;
            // Emit event to parent that video finished
            this.$emit("video-completed");
        },
    },
};
</script>

<style scoped>
/* Loading Container */
.loading-container {
    position: fixed;
    inset: 0;
    z-index: 10000;
}

/* ===== SVG Animation Container ===== */
.svg-animation-container {
    width: 100%;
    height: 100%;
    background: #eeeeee;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    gap: 10px;
}

/* ===== Video Container ===== */
.loading-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    background: #000;
}

/* ===== SVG Animation Styles ===== */
.loading-container {
    --duration: 8s;
    --easing: linear;

    --mid-duration: calc(var(--duration) * 7);
    --far-duration: calc(var(--duration) * 12);

    --track-bg: #002a541a;
    --fill-bg: #002a54;

    --frog-end-offset: 45px;
}

/* ===== Scene ===== */
.scene {
    position: relative;
    width: 100%;
    height: 65px;
    background: linear-gradient(to bottom, transparent 70%, #002a540d 70% 100%);
}

.layer {
    position: absolute;
    inset: 0;
    background-repeat: repeat-x;
    background-position: 0 100%;
    will-change: background-position;
    pointer-events: none;
}

/* FAR layer: smaller / slower / lighter */
.layer--far {
    --tile-w: 110px;
    background-size: var(--tile-w) auto;
    animation: scroll-far var(--far-duration) linear infinite;
    opacity: 0.38;
    filter: blur(0.3px);
    /* BASE64 version (safe in SFC styles) */
    background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMTAiIGhlaWdodD0iNzIiIHZpZXdCb3g9IjAgMCAxMTAgNzIiPgogIDxwb2x5Z29uIHBvaW50cz0iNTUsOCA4MCwzNiAzMCwzNiIgZmlsbD0iIzVjOGQ0ZSIvPgogIDxwb2x5Z29uIHBvaW50cz0iNTUsMTggOTAsNTggMjAsNTgiIGZpbGw9IiM1YzhkNGUiLz4KICA8cmVjdCB4PSI1MSIgeT0iNTgiIHdpZHRoPSI4IiBoZWlnaHQ9IjEwIiBmaWxsPSIjM2U0ZTNjIi8+Cjwvc3ZnPg==");
}

/* MID layer: closer / faster */
.layer--mid {
    --tile-w: 140px;
    background-size: var(--tile-w) auto;
    animation: scroll-mid var(--mid-duration) linear infinite;
    opacity: 0.7;
    /* BASE64 version (safe in SFC styles) */
    background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNDAiIGhlaWdodD0iNzIiIHZpZXdCb3g9IjAgMCAxNDAgNzIiPgogIDxwb2x5Z29uIHBvaW50cz0iNzAsNiAxMDAsMzggNDAsMzgiIGZpbGw9IiMzYzdhM2MiLz4KICA8cG9seWdvbiBwb2ludHM9IjcwLDE4IDEwOCw1OCAzMiw1OCIgc3R5bGU9IiIgZmlsbD0iIzNjN2EzYyIvPgogIDxyZWN0IHg9IjY2IiB5PSI1OCIgd2lkdGg9IjgiIGhlaWdodD0iMTAiIGZpbGw9IiMzZTRlM2MiLz4KPC9zdmc+");
}

@keyframes scroll-mid {
    from {
        background-position-x: 0;
    }
    to {
        background-position-x: calc(var(--tile-w) * -1);
    }
}
@keyframes scroll-far {
    from {
        background-position-x: 0;
    }
    to {
        background-position-x: calc(var(--tile-w) * -1);
    }
}

/* ===== Frog motion ===== */
.frog-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 1;
}

.frog-mover {
    width: 100%;
    will-change: transform;
    animation: frog-move var(--duration) var(--easing) infinite;
}

.frog {
    display: block;
    height: 40px;
    margin-top: 35px;
    user-select: none;
    pointer-events: none;
    position: relative;
}

/* ===== Speech Bubble ===== */
.speech-bubble {
    position: absolute;
    top: -50px;
    left: 2%;
    transform: translateX(-50%);
    background: white;
    border: 2px solid #002a54;
    border-radius: 12px;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: bold;
    color: #002a54;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    pointer-events: none;
    min-width: 60px;
    text-align: center;
}

/* Speech bubble tail pointing down */
.speech-bubble::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid white;
}

.speech-bubble::before {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
    border-top: 9px solid #002a54;
    z-index: -1;
}

.bubble-text {
    display: inline-block;
}

.word {
    opacity: 0;
    display: inline-block;
    margin-right: 4px;
}

.word:last-child {
    margin-right: 0;
}

.word-1 {
    animation: word-cycle-1 3s ease-in-out infinite;
    animation-delay: 1s;
}

.word-2 {
    animation: word-cycle-2 3s ease-in-out infinite;
    animation-delay: 1.5s;
}

.word-3 {
    animation: word-cycle-3 3s ease-in-out infinite;
    animation-delay: 2s;
}

/* Word 1: appears at 1s, disappears at 3s (67% of 3s) */
@keyframes word-cycle-1 {
    0% {
        opacity: 0;
        transform: translateY(5px);
    }
    5% {
        opacity: 1;
        transform: translateY(0);
    }
    67% {
        opacity: 1;
        transform: translateY(0);
    }
    72% {
        opacity: 0;
        transform: translateY(-5px);
    }
    100% {
        opacity: 0;
        transform: translateY(5px);
    }
}

/* Word 2: appears at 1.5s, disappears at 3s (50% of 3s) */
@keyframes word-cycle-2 {
    0% {
        opacity: 0;
        transform: translateY(5px);
    }
    5% {
        opacity: 1;
        transform: translateY(0);
    }
    50% {
        opacity: 1;
        transform: translateY(0);
    }
    55% {
        opacity: 0;
        transform: translateY(-5px);
    }
    100% {
        opacity: 0;
        transform: translateY(5px);
    }
}

/* Word 3: appears at 2s, disappears at 3s (33% of 3s) */
@keyframes word-cycle-3 {
    0% {
        opacity: 0;
        transform: translateY(5px);
    }
    5% {
        opacity: 1;
        transform: translateY(0);
    }
    33% {
        opacity: 1;
        transform: translateY(0);
    }
    38% {
        opacity: 0;
        transform: translateY(-5px);
    }
    100% {
        opacity: 0;
        transform: translateY(5px);
    }
}

@keyframes frog-move {
    0% {
        transform: translateX(-20%);
    }
    100% {
        transform: translateX(calc(100% - var(--frog-end-offset)));
    }
}

/* ===== Progress bar (short repeating lines) ===== */
.line-track {
  position: relative;
  width: 100%;
  height: 3px;
  background: transparent;
  overflow: hidden;
}

.line-fill {
  position: absolute;
  top: 0;
  right: 0; /* Start from the right side */
  width: 200%; /* double width so pattern can loop */
  height: 100%;
  background-image: repeating-linear-gradient(
    to right,
    var(--fill-bg) 0 150px,   /* dash length */
    transparent 150px 200px   /* gap length */
  );
  background-repeat: repeat-x;
  animation: line-move 3s linear infinite;
}

/* move pattern to the left to simulate ground motion */
@keyframes line-move {
  from {
    background-position-x: 0;
  }
  to {
    background-position-x: -200px; /* same as dash+gap */
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
    .frog-mover,
    .line-fill,
    .layer--mid,
    .layer--far {
        animation-duration: calc(var(--duration) * 2);
        animation-timing-function: linear;
    }
    .layer--far {
        filter: none;
    }
}
</style>
